import { RegenerationRequestError } from './regenerationRequestError';
/**
 * Content Review Service
 *
 * Frontend API client for the Content Review Dashboard (Story 29).
 * Provides access to pending reviews, metrics, queue, and approval workflows.
 *
 * Date: December 16, 2025
 */

import { authFetch } from '@/utils/auth-fetch';
import { AuthFetchError } from '@/utils/auth-fetch-error';

// Types
export interface PendingItem {
  id: string;
  title: string;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  createdAt: string;
  siteName: string;
}

export interface PendingResponse {
  success: boolean;
  data: {
    totalPending: number;
    items: PendingItem[];
  };
}

export interface ReviewMetrics {
  discoveryIndexingFailed?: number;
  totalPending: number;
  reviewedInPeriod: number;
  newInPeriod: number;
  approvalRate: number;
  rejectionRate: number;
  approvedCount: number;
  rejectedCount: number;
  byContentType: {
    ai_article: number;
    product_aeo: number;
    category_aeo: number;
  };
  topReviewers: Array<{
    userId: string;
    name: string;
    count: number;
  }>;
}

export interface MetricsResponse {
  success: boolean;
  data: ReviewMetrics;
}

export interface PreviousRejection {
  reason: string;
  rejectedAt: string;
  rejectedBy: string | null;
  totalRejections: number;
}

export interface QueueItem {
  id: string;
  title: string;
  /** Feed SKU (product_feed_items.product_id). Names are not unique — 41% of the catalogue is
   *  un-targetable by name (F1/#4991) — so this is the identifier an operator can actually select
   *  a cohort by before applying a force flag. */
  sku?: string | null;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  status: 'pending' | 'approved' | 'rejected' | 'needs_research';
  createdAt: string;
  updatedAt: string;
  siteId: string;
  siteName: string;
  approvedBy: string | null;
  reviewerName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  faqQaPassed: boolean | null;
  faqCount: number | null;
  // Previous rejection info (for items that were regenerated after rejection)
  previousRejection: PreviousRejection | null;
  // Needs manual review (e.g., suspicious data flagged for human verification)
  needsManualReview: boolean;
  // task-pu60uzfmq2: the generation-time cross-section consistency detector. It has always run and
  // stamped its findings; nothing read them, so a page whose own detector recorded a HIGH
  // contradiction looked identical to a clean one. These are NOT a blocking signal — the block rule
  // is a separate product decision — they exist so the human gate can see what the machine found.
  consistencyViolationCount?: number;
  consistencyMaxSeverity?: 'critical' | 'high' | 'medium' | 'low' | null;
  consistencyViolations?: ConsistencyViolation[] | null;
  // Product category (only for product_aeo)
  category: string | null;
  // Is inactive: product no longer in active feed (only for product_aeo)
  isInactive?: boolean;
  // Product image URL for queue thumbnail (product_aeo only; null for other types)
  imageUrl?: string | null;
}

/** One finding from the generation-time consistency detector (AEOProductPageService._checkCrossSectionConsistency). */
export interface ConsistencyViolation {
  type?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | string;
  message?: string;
  sections?: string[];
  evidence?: string;
}

export interface QueueFilters {
  siteId?: string | null;
  contentType?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  reviewerId?: string | null;
  faqQaPassed?: boolean | null;
  hasPreviousRejection?: boolean | null; // Filter for items with rejection history
  needsManualReview?: boolean | null; // Filter for items flagged for manual review (e.g., suspicious data)
  includeInactive?: boolean; // Include products no longer in active feed (for managing legacy AEO)
  includePublished?: boolean; // Include published content in results (for reviewing what's live)
  search?: string | null; // Search by product name or product ID
  urlContains?: string | null; // Substring match on product URL (paste a product URL or its embedded ID); product_aeo only
  category?: string | null; // Filter by product category path
  // task-8xq63qswed: the facets the data already counts (see pages/content-review/facetFilters.ts). They live HERE,
  // in the one filter object, so the queue, Select All and the facet counts serialise them through the shared
  // declaration below and the three sets are one set.
  validation?: 'passed' | 'failed' | null;
  issue?: string | null;
  gate?: 'terminal' | 'clear' | null;
  specRows?: '4plus' | 'le3' | 'none' | null;
  firstTime?: boolean | null;
  preset?: 'approvable' | null;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface QueueResponse {
  success: boolean;
  data: {
    items: QueueItem[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
  };
}

export interface AvailableLanguage {
  language: string;
  isSource: boolean;
  available: boolean;
}

export interface ContentDetails {
  id: string;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  siteId: string;
  siteName: string;
  createdAt: string;
  updatedAt: string;
  approvedBy: string | null;
  reviewerName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  title: string;
  content?: string;
  contentHtml?: string;
  metadata?: Record<string, unknown>;
  status: string;
  // product_aeo only — language selector context
  currentLanguage?: string;
  sourceLanguage?: string;
  availableLanguages?: AvailableLanguage[];
  expectedSectionKeys?: string[];
  [key: string]: unknown;
}

export interface ContentDetailsResponse {
  success: boolean;
  data: ContentDetails;
}

export interface ApprovalResult {
  success: boolean;
  data: {
    id: string;
    contentType: string;
    reviewedBy: string;
    reviewedAt: string;
    status: string;
  };
}

export interface RejectionResult {
  success: boolean;
  data: {
    id: string;
    contentType: string;
    // Null for a data-quality downgrade: the approval pair is cleared and the reviewer
    // attribution lives in content_review_history (GH #3214).
    reviewedBy: string | null;
    reviewedAt: string | null;
    status: string;
    reason: string;
    // True only when a data_quality_issues row was actually created (product_aeo flagged
    // rejects). Used by the UI to avoid claiming a flag was recorded when it was not.
    dataQualityIssueRecorded?: boolean;
  };
}

export interface JSONLDResponse {
  success: boolean;
  data: Record<string, unknown>;
}

// AI Analysis Types
export interface RecommendedSolution {
  type: 'domain_knowledge' | 'data_fix' | 'code_bug';
  reason: string;
  knowledge_type?: 'terminology' | 'specification' | 'context' | 'expertise_signal' | 'validation_rule';
  suggested_scope?: {
    level: 'category' | 'brand' | 'global';
    value: string | null;
  };
  estimated_impact?: number;
}

export interface ContentIssue {
  type: 'TERMINOLOGY' | 'SPECIFICATION' | 'MISSING_CONTEXT' | 'EXPERTISE_SIGNAL' | 'HALLUCINATION' | 'LOGIC_ERROR';
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  location: string;
  suggestion: string;
  recommended_solution?: RecommendedSolution;
}

export interface KnowledgeGapSuggestion {
  gap: string;
  scope: 'SECTOR' | 'CATEGORY' | 'BRAND' | 'ATTRIBUTE';
  scopeValue: string;
  researchQuestion: string;
  proposedKnowledge: string;
}

export interface ContentAnalysis {
  QUALITY_SCORE: number;
  RECOMMENDATION: 'APPROVE' | 'APPROVE_WITH_NOTES' | 'REJECT';
  SUMMARY: string;
  STRENGTHS: string[];
  ISSUES_FOUND: ContentIssue[];
  KNOWLEDGE_GAPS: KnowledgeGapSuggestion[];
  REVIEWER_NOTES: string[];
  CONFIDENCE: 'HIGH' | 'MEDIUM' | 'LOW';
}

/**
 * ⛔⛔ WHETHER THE JUDGE ACTUALLY HAD ITS KNOWLEDGE — a separate fact from whether it ran.
 *
 * `success` means the analysis executed. It is `true` even when every knowledge source was down, so
 * a verdict grounded in nothing is indistinguishable from a fully grounded one unless this field is
 * read. Four states, and the third and fourth are the ones that used to be invisible:
 *   ok: true                                    -> grounded
 *   KNOWLEDGE_SOURCES_PARTIAL_FAILURE           -> some sources loaded; NOT the same as grounded
 *   KNOWLEDGE_SOURCES_ALL_FAILED                -> the judge saw no rules at all
 *   KNOWLEDGE_RETRIEVAL_FAILED / _UNREPORTED    -> retrieval threw, or the service said nothing
 *
 * ⚠️ OPTIONAL BECAUSE THE FIELD IS NEWER THAN SOME DEPLOYED BACKENDS, NOT because it is decorative.
 * An absent field means "we do not know", which the UI must never render as "it worked".
 */
export interface KnowledgeRetrievalSignal {
  ok: boolean;
  code?: string;
  failedSources?: string[];
  attemptedSources?: number;
}

export interface AnalysisResponse {
  success: boolean;
  contentId: string;
  contentType: string;
  analysis: ContentAnalysis;
  knowledgeRetrieval?: KnowledgeRetrievalSignal;
  proposal?: {
    title: string;
    knowledgeType: string;
    knowledgeText: string;
    scopeLevel: string;
    scopeValue: string | null;
    researchQuestion: string;
    researchMethodology: string;
    gapAnalysis: Record<string, unknown>;
    allIssues: ContentIssue[];
    allKnowledgeGaps: KnowledgeGapSuggestion[];
  };
}

export interface KnowledgeProposalResult {
  success: boolean;
  feedback: {
    id: string;
    contentType: string;
    contentId: string;
  };
  knowledgeProposals: Array<{
    id: string;
    title: string;
    knowledgeType: string;
    scopeLevel: string;
    status: string;
  }>;
}

// API Functions
export async function getPendingItems(
  siteId?: string | null,
  limit: number = 5
): Promise<PendingResponse> {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  params.set('limit', limit.toString());

  const response = await authFetch(`/content-review/pending?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch pending items');
  }

  return response.json();
}

export async function getMetrics(filters?: {
  siteId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  // The badge this feeds is rendered directly above the queue and refreshed by the
  // same effect, so it has to read the inactive toggle the queue reads. Serialised
  // with the same spelling buildFilterParams uses below, deliberately.
  includeInactive?: boolean;
}): Promise<MetricsResponse> {
  const params = new URLSearchParams();
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.includeInactive) params.set('includeInactive', 'true');

  const response = await authFetch(`/content-review/metrics?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch metrics');
  }

  return response.json();
}

/**
 * ⛔⛔ THE SINGLE DECLARATION OF THE CONTENT-REVIEW FILTER SET. BOTH the queue (what the operator SEES)
 * and the queue-ids selection (what a bulk action ACTS ON) serialise their filters through here, and
 * NEITHER may serialise a filter of its own.
 *
 * This exists because the two paths used to hold SEPARATE hand-maintained copies of this block, so
 * they diverged by default and converged only by memory. `includePublished` is what that cost: the
 * route honoured it, the service forwarded it, and the queue showed published rows while "Select All"
 * silently left them out — a destructive bulk control whose selected set differed from the displayed
 * set, with nothing reporting the discrepancy (task-u0qpjf2q4m, then task-gkula84yct for this class).
 *
 * ⇒ SO IF YOU ARE ADDING A FILTER, ADD IT HERE AND ONLY HERE. Adding it to one caller and not the
 * other is the bug this function exists to make impossible.
 * ⚠️ PAGINATION AND SORT ARE DELIBERATELY NOT HERE — they belong to the queue's presentation, not to
 * the filter set, and a cross-page selection has no page. getQueue appends them itself.
 */
function appendQueueFilterParams(params: URLSearchParams, filters?: QueueFilters): void {
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.contentType) params.set('contentType', filters.contentType);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.reviewerId) params.set('reviewerId', filters.reviewerId);
  if (filters?.faqQaPassed !== undefined && filters?.faqQaPassed !== null) {
    params.set('faqQaPassed', filters.faqQaPassed.toString());
  }
  if (filters?.hasPreviousRejection !== undefined && filters?.hasPreviousRejection !== null) {
    params.set('hasPreviousRejection', filters.hasPreviousRejection.toString());
  }
  if (filters?.needsManualReview !== undefined && filters?.needsManualReview !== null) {
    params.set('needsManualReview', filters.needsManualReview.toString());
  }
  if (filters?.includeInactive) {
    params.set('includeInactive', 'true');
  }
  if (filters?.includePublished) {
    params.set('includePublished', 'true');
  }
  if (filters?.search) params.set('search', filters.search);
  if (filters?.urlContains) params.set('urlContains', filters.urlContains);
  if (filters?.category) params.set('category', filters.category);
  // task-8xq63qswed: the facets, same names as the backend declaration; an unset facet is not sent.
  if (filters?.validation) params.set('validation', filters.validation);
  if (filters?.issue) params.set('issue', filters.issue);
  if (filters?.gate) params.set('gate', filters.gate);
  if (filters?.specRows) params.set('specRows', filters.specRows);
  if (filters?.firstTime !== undefined && filters?.firstTime !== null) {
    params.set('firstTime', filters.firstTime.toString());
  }
  if (filters?.preset) params.set('preset', filters.preset);
}

/**
 * task-8xq63qswed — the facet COUNTS under the current filters (GET /queue/facets): per dimension the counts of
 * its values under every OTHER active facet, plus `matching` (all active facets) and `approvable` (the preset).
 * Serialised through the SAME declaration as the queue and Select All, so the numbers describe the set the
 * operator sees and would act on.
 */
export interface FacetCountsResponse {
  success: boolean;
  data: {
    total: number;
    matching: number;
    approvable: number;
    counts: Partial<Record<'validation' | 'issue' | 'gate' | 'specRows' | 'firstTime', Record<string, number>>>;
    active: Record<string, unknown>;
    truncated: boolean;
  };
}

export async function getQueueFacets(filters?: QueueFilters): Promise<FacetCountsResponse> {
  const params = new URLSearchParams();
  appendQueueFilterParams(params, filters);
  const response = await authFetch(`/content-review/queue/facets?${params}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch queue facets');
  }
  return response.json();
}

export async function getQueue(filters?: QueueFilters): Promise<QueueResponse> {
  const params = new URLSearchParams();

  appendQueueFilterParams(params, filters);

  if (filters?.page) params.set('page', filters.page.toString());
  if (filters?.limit) params.set('limit', filters.limit.toString());
  if (filters?.sortBy) params.set('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);

  // Add cache-busting parameter to prevent browser caching
  params.set('_t', Date.now().toString());

  const response = await authFetch(`/content-review/queue?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch queue');
  }

  return response.json();
}

export interface QueueIdsResponse {
  success: boolean;
  data: {
    ids: Array<{ id: string; contentType: string }>;
    total: number;
    truncated: boolean;
  };
}

/**
 * Get all queue item IDs matching filters (lightweight endpoint for "Select All")
 * Returns only id and contentType to minimize data transfer
 */
export async function getQueueIds(filters?: Omit<QueueFilters, 'page' | 'limit' | 'sortBy' | 'sortOrder'>): Promise<QueueIdsResponse> {
  const params = new URLSearchParams();

  appendQueueFilterParams(params, filters);

  const response = await authFetch(`/content-review/queue/ids?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch queue IDs');
  }

  return response.json();
}

export interface CategoryWithCount {
  category: string;
  productCount: number;
}

export interface CategoriesResponse {
  success: boolean;
  data: CategoryWithCount[];
}

/**
 * Get categories that have products with AEO content for the filter dropdown
 */
export async function getProductCategories(siteId: string): Promise<CategoriesResponse> {
  const response = await authFetch(`/content-review/product-categories-with-aeo/${siteId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch categories');
  }

  return response.json();
}

export async function getContentDetails(
  contentType: string,
  contentId: string,
  siteId: string,
  language?: string | null
): Promise<ContentDetailsResponse> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content access');
  }
  // Add timestamp to prevent browser caching (query param only - headers cause CORS issues)
  const timestamp = Date.now();
  const langParam = language ? `&language=${encodeURIComponent(language)}` : '';
  const response = await authFetch(`/content-review/${contentType}/${contentId}?siteId=${siteId}${langParam}&_t=${timestamp}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch content details');
  }

  return response.json();
}

export async function approveContent(
  contentType: string,
  contentId: string,
  siteId: string,
  notes?: string
): Promise<ApprovalResult> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content approval');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes, siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve content');
  }

  return response.json();
}

/**
 * task-edbnz2qj5c — the kind of reject, a code the backend binds to:
 *   plain        — archive as rejected; refused on a published page (409).
 *   content      — pull a published page back to review for regeneration; NO data issue recorded.
 *   data_quality — pull it back AND record a data quality issue against the product (the legacy
 *                  flagAsDataIssue checkbox).
 */
export type RejectKind = 'plain' | 'content' | 'data_quality';

export async function rejectContent(
  contentType: string,
  contentId: string,
  siteId: string,
  reason: string,
  flagAsDataIssue: boolean = false,
  rejectKind?: RejectKind
): Promise<RejectionResult> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content rejection');
  }
  // The kind is the contract; flagAsDataIssue is its legacy spelling and is sent consistently
  // with it so the backend's contradiction guard never fires from this client.
  const kind: RejectKind = rejectKind ?? (flagAsDataIssue ? 'data_quality' : 'plain');
  const response = await authFetch(`/content-review/${contentType}/${contentId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, siteId, flagAsDataIssue: kind === 'data_quality', rejectKind: kind }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject content');
  }

  return response.json();
}

export async function updateRejectionReason(
  contentType: string,
  contentId: string,
  siteId: string,
  reason: string
): Promise<{ success: boolean; data: { id: string; contentType: string; rejectionReason: string; previousReason: string | null } }> {
  if (!siteId) {
    throw new Error('Site ID is required for rejection reason update');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/rejection-reason`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update rejection reason');
  }

  return response.json();
}

export async function resetToPending(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<{ success: boolean; data: { id: string; contentType: string; status: string } }> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content reset');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reset content to pending');
  }

  return response.json();
}

// Review History Types
export interface ReviewHistoryItem {
  id: string;
  action: 'approved' | 'rejected' | 'reset' | 'needs_research' | 'rejection_edited';
  reason: string | null;
  reviewedAt: string;
  contentVersion: number;
  metadata: Record<string, unknown>;
  reviewerName: string | null;
  reviewerEmail: string | null;
}

export interface ReviewHistoryResponse {
  success: boolean;
  data: {
    items: ReviewHistoryItem[];
    total: number;
  };
}

/**
 * Get full review history for a content item
 * Shows all approval/rejection/reset actions over time
 */
export async function getReviewHistory(
  contentType: string,
  contentId: string
): Promise<ReviewHistoryResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/history`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get review history');
  }

  return response.json();
}

export interface BulkApproveItem {
  contentType: string;
  id: string;
}

export interface BulkApproveResult {
  success: boolean;
  approved: BulkApproveItem[];
  failed: Array<BulkApproveItem & { error: string }>;
  // task-8xq63qswed: ids the press refused because their CURRENT row failed the active exclusion (with reasons).
  refused?: Array<BulkApproveItem & { reasons: string[] }>;
  totalRequested: number;
  message: string;
  reviewerId: string;
}

// Bulk Regeneration Types
export interface BulkRegenerateItem {
  contentType: string;
  id: string;
}

export interface BulkRegenerateBatchResult {
  success: boolean;
  mode: 'batch';
  runId: string;
  totalProducts: number;
  configuredModel?: string;
  batchProvider?: 'openai' | 'vertexai';
  message: string;
  statusUrl: string;
}

/**
 * ⛔⛔ THIS IS A HANDLE, NOT A RESULT — AND THAT CHANGE IS THE WHOLE FIX.
 * Real-time regeneration runs ~90-120s PER PRODUCT sequentially, behind a 60s proxy read timeout.
 * The endpoint used to hold the connection open for the whole cohort, so the proxy cut it at 60s
 * and returned an HTML error page that this client parsed as JSON ("Unexpected token '<'").
 * It now responds 202 with a job id and finishes the work in the background.
 * 🔑 THE OLD TYPE CARRIED `regenerated` / `failed` / `details`, WHICH ARE NOT KNOWN AT 202 TIME.
 *    Keeping those fields would have let the UI keep reporting "Regeneration Complete" the instant
 *    the request returned — which is what it did, and it was wrong even before the timeout: a 2xx
 *    on this route has never meant the work was done. The fields are REMOVED rather than made
 *    optional so a consumer reading them breaks at COMPILE time instead of silently reading
 *    `undefined`.
 * ⛔⛔ BUT DO NOT TRUST THAT AS THE SAFETY NET — I INITIALLY WROTE THAT IT MAKES "EVERY CONSUMER
 *    BREAK", AND IT DOES NOT. `message` survives on both shapes, and the one consumer that
 *    mattered (ContentReviewDashboard.product.tsx) only read `result.message` — so it kept
 *    compiling AND kept announcing "Regeneration Complete" on a 202. A type change only protects
 *    the fields it removes; the caller that reads the SURVIVING field is exactly the one still
 *    telling the operator the wrong thing, and it has to be fixed by hand.
 * ⇒ The real outcome arrives via `RealtimeRegenJob.result` when the job reaches a terminal state.
 */
export interface BulkRegenerateRealtimeResult {
  success: boolean;
  mode: 'realtime';
  async: true;
  jobId: string;
  totalProducts: number;
  statusUrl: string;
  message: string;
}

/** Per-product outcome, as recorded by the backend loop. */
export interface RealtimeRegenDetail {
  productId: string;
  status: 'success' | 'failed' | 'deferred' | string;
  error?: string;
  reason?: string;
  gateBlocked?: boolean;
  gateType?: string;
}

/**
 * The polled job. `status: 'completed'` means THE COHORT RAN — not that every product succeeded.
 * ⚠️ A mixed cohort (some regenerated, some gate-blocked) is a COMPLETED job with failures in
 * `result.details`; collapsing it to a single pass/fail is what made the old response useless.
 */
/**
 * ⚠️ THIS MIRRORS THE *API* SHAPE (routes/jobs.js:63-80), NOT THE async_jobs ROW.
 * I first typed this from the DB columns — `progress_percent`, `progress_message`, `error_message`,
 * `id` — and every one of them is wrong on the wire: the route wraps them as `progress: {percent,
 * message}`, renames `id` to `jobId`, and only attaches `result`/`error` on the terminal statuses.
 * 🔑 Reading the row shape would have compiled fine and produced a bar frozen at 0% with a blank
 *    label and no error — the exact "polls forever with no terminal state" failure this change
 *    exists to prevent, delivered by the fix itself. The contract is the ROUTE, not the table.
 */
export interface RealtimeRegenJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  jobType: string;
  progress: { percent: number; message: string };
  createdAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  /** Present only when status === 'completed'. */
  result?: {
    totalProducts: number;
    regenerated: number;
    failed: number;
    deferred: number;
    message: string;
    details: RealtimeRegenDetail[];
  };
  /** Present only when status === 'failed'. */
  error?: string;
}

/**
 * ⛔⛔ THE OPERATOR OVERRIDE MAY ONLY RELEASE THE RUN IT IS BEING SHOWN FOR.
 *
 * codex-reviewer's finding: starting a run never cleared the previous run's outcome card, so the
 * "Allow a new run anyway" button on run A's stale result would release the guard held by run B —
 * the run currently generating. The operator sees a finished cohort's summary and an escape hatch
 * beside it; nothing on screen says the hatch belongs to a different run than the one in flight.
 *
 * ⚠️ THE SERVER-SIDE SLOT ALREADY STOPS THE MONEY: the next press hits a 409 and attaches instead
 * of launching a second cohort. This is not that. This is the guard refusing to lie about WHICH run
 * it is guarding — and it is written as a predicate rather than as "remember to clear the outcome"
 * precisely because the clearing is the thing that was forgotten in the first place. A rule enforced
 * at the point of use survives the next person who adds a fourth way to set an outcome.
 */
export function overrideReleasesGuard(
  outcomeRunId: string | null | undefined,
  guardedRunId: string | null
): boolean {
  // ⚠️ ONE GUARD, NOT TWO, DELIBERATELY. This was written as two early returns — `!guardedRunId`
  // then `!outcomeRunId` — and mutation testing showed each one SURVIVING its own deletion, because
  // the equality below already returns false for either null alone. They were not redundant
  // together: they jointly stopped `(null, null)` from comparing equal and releasing a guard that
  // names no run. So each was masking the other, and no test could see either. Collapsed into a
  // single condition that a single test can kill.
  if (!outcomeRunId || !guardedRunId) return false;
  return outcomeRunId === guardedRunId;
}

/**
 * Poll a real-time regeneration job.
 * ⚠️ Throws on a non-OK response. The caller MUST treat a throw as "poll failed", NOT as "the job
 * failed" — the cohort keeps running in the backend regardless of whether this fetch succeeded,
 * and reporting a transient 502 as a failed regeneration is the same class of lie the 504 caused.
 */
export async function getRealtimeRegenJob(siteId: string, jobId: string): Promise<RealtimeRegenJob> {
  // ⛔⛔ NOT /jobs/:jobId — THAT ROUTE IS CREATOR-SCOPED (routes/jobs.js:57 fetches with
  // getJob(jobId, req.user.id)). Attachment is SITE-scoped, so a second authorised user on the same
  // site would attach to a handle they can never poll: 404 every tick, then "lost track of this
  // run", while the server-side slot correctly keeps refusing them a new run. Locked out AND blind.
  // The site-authorised endpoint serves this one job type and is scoped by checkSiteAccess.
  const response = await authFetch(`/content-review/realtime-regen/job/${siteId}/${jobId}`);
  if (!response.ok) {
    throw new Error(`Job status poll failed (${response.status})`);
  }
  return (await response.json()) as RealtimeRegenJob;
}

export type BulkRegenerateResult = BulkRegenerateBatchResult | BulkRegenerateRealtimeResult;

/**
 * Bulk approve multiple content items
 * The current user becomes the reviewer for all approved items (for citation)
 */
/** The facet subset the press carries (the shape of pages/content-review/facetFilters.ts's FacetFilters). */
export interface BulkApproveExclusion {
  validation?: string | null;
  issue?: string | null;
  gate?: string | null;
  specRows?: string | null;
  firstTime?: boolean | null;
  preset?: string | null;
}

export async function bulkApprove(
  items: BulkApproveItem[],
  siteId: string,
  notes?: string,
  // task-8xq63qswed: the ACTIVE facets travel with the press. The route re-reads every product_aeo id against them
  // at press time and refuses, by name, any row that no longer passes — a page that gained a hold between the
  // listing and the click is never approved silently. Omitted (or empty) = the route behaves as before.
  // (Round 2: typed as the facet shape — an interface is not assignable to Record<string, unknown>, and tsc said so
  // at line 1184 of the dashboard on the previous head; the round-1 "no error in the touched files" was wrong.)
  exclusion?: BulkApproveExclusion | null
): Promise<BulkApproveResult> {
  if (!siteId) {
    throw new Error('Site ID is required for bulk approval');
  }
  const body: Record<string, unknown> = { items, siteId, notes };
  if (exclusion && Object.keys(exclusion).length > 0) body.exclusion = exclusion;
  const response = await authFetch('/content-review/bulk-approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to bulk approve content');
  }

  return response.json();
}

/**
 * Bulk regenerate multiple content items
 * Uses either batch mode (SubBatchProcessorService) or real-time mode (AEOProductPageService)
 *
 * @param items - Array of items to regenerate (must have contentType and id)
 * @param mode - 'batch' for cost-efficient batch API (~50% savings, ~24h), 'realtime' for immediate (max 10 items)
 * @param siteId - Site ID for multi-tenancy
 */
/**
 * task-fzfuulrr28 — what happens to the regenerated pages when the run COMPLETES (applied by the
 * sweeper, not at dispatch). Persisted as processing_options.regen_outcome on the run's config.
 *   leave_needs_review          — default: pages come back needs_review, approved by hand
 *   approve_previously_approved — pages that were approved before the run are re-approved
 *   approve_all                 — every generated page is approved; validator-refused pages stay
 */
export type RegenOutcome = 'leave_needs_review' | 'approve_previously_approved' | 'approve_all';
export const REGEN_OUTCOMES: RegenOutcome[] = ['leave_needs_review', 'approve_previously_approved', 'approve_all'];
export const REGEN_OUTCOME_LABELS: Record<RegenOutcome, string> = {
  leave_needs_review: 'Leave as needs review',
  approve_previously_approved: 'Re-approve previously approved',
  approve_all: 'Approve all',
};

export interface BulkRegenerateForceOptions {
  /** Re-run AI enrichment before regenerating. Persisted as processing_options.force_ai_enrichment. */
  forceReEnrich?: boolean;
  /** task-fzfuulrr28: completion outcome. Omitted → the server default (leave_needs_review). */
  outcome?: RegenOutcome;
  /** Re-run PBS benefit scoring. Persisted as processing_options.force_rescore. BATCH ONLY —
   *  the route returns 400 for mode='realtime' (routes/content-review.js:2153-2163). */
  forceRescore?: boolean;
}

export async function bulkRegenerate(
  items: BulkRegenerateItem[],
  mode: 'batch' | 'realtime',
  siteId: string,
  force: BulkRegenerateForceOptions = {}
): Promise<BulkRegenerateResult> {
  if (!siteId) {
    throw new Error('Site ID is required for bulk regeneration');
  }

  // ⛔ These two flags have been accepted by the backend since 2026-05-12 and were NEVER SENT from
  // here — the body was `{ items, mode, siteId }`, so both were permanently false from the
  // dashboard. The server-side capability was complete and unreachable: a capability with no
  // button. Verified honoured end-to-end before wiring (forceReEnrich ->
  // SubBatchProcessorService:2754/2786/2816; forceRescore -> :4152 with a hard contract at :4180
  // that fails the sub-batch rather than regenerating on stale scores).
  // ⛔ THROW, DO NOT SILENTLY COERCE. This used to send `forceRescore: mode === 'batch' && ...`,
  // which quietly turned a caller's true into a false — the request SENT differed from the request
  // MADE, with nothing telling anyone. That is the same inert-control class this whole change is
  // removing, one level down: I had called the coercion a virtue ("the UI explains, the service
  // enforces") when it was a lie the caller could not see.
  // The dialog already DISABLES rescore in realtime, so no operator reaches this; it is defence at
  // the service boundary for any non-dialog caller, and it fails LOUDLY.
  if (mode === 'realtime' && force.forceRescore === true) {
    throw new Error(
      'forceRescore is batch-only: the real-time path does not re-run PBS scoring ' +
      '(the API rejects this combination with a 400). Use mode="batch" for force-rescore.'
    );
  }

  const response = await authFetch('/content-review/bulk-regenerate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items,
      mode,
      siteId,
      // task-fzfuulrr28: sent as chosen; the server 400s an unknown value rather than defaulting.
      outcome: force.outcome ?? 'leave_needs_review',
      forceReEnrich: force.forceReEnrich === true,
      forceRescore: force.forceRescore === true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    // ⛔⛔ A 409 IS NOT A PLAIN FAILURE — IT CARRIES A HANDLE, AND FLATTENING IT TO `new Error(msg)`
    // THROWS THAT HANDLE AWAY. The server-side guard refuses a second cohort for this site and hands
    // back the run already in flight; if the caller only learns "no", the operator sees a refusal
    // with no progress anywhere, concludes the button is broken, and presses again — which is the
    // exact behaviour the guard exists to stop. The typed error is what lets the dashboard ATTACH.
    if (response.status === 409 && error.code === 'realtime_regen_already_active') {
      throw new RealtimeRegenAlreadyActiveError(error);
    }
    throw new Error(error.error || 'Failed to bulk regenerate content');
  }

  return response.json();
}

/** Thrown when the site already has a real-time cohort running. Carries the run so it can be joined. */
export class RealtimeRegenAlreadyActiveError extends Error {
  readonly jobId: string;
  readonly statusUrl: string;
  readonly progress: { percent: number; message: string };

  constructor(body: {
    error?: string;
    jobId: string;
    statusUrl: string;
    progress?: { percent: number; message: string };
  }) {
    super(body.error || 'A real-time regeneration is already running for this site.');
    this.name = 'RealtimeRegenAlreadyActiveError';
    this.jobId = body.jobId;
    this.statusUrl = body.statusUrl;
    this.progress = body.progress || { percent: 0, message: '' };
  }
}

/**
 * Ask whether this site has a real-time cohort in flight — the REHYDRATE half of the server-side
 * guard. Called on load so a dashboard that was reloaded (or opened in a second tab) mid-run
 * re-attaches its progress bar instead of being locked out of a button with nothing to look at.
 *
 * ⚠️ Returns `{ active: false }` rather than throwing when there is no run: absence is the normal
 * answer here, and a throw would make every clean load look like a failure in the console.
 */
export async function getActiveRealtimeRegen(
  siteId: string
): Promise<
  | { active: false }
  | { active: true; jobId: string; statusUrl: string; progress: { percent: number; message: string } }
> {
  const response = await authFetch(`/content-review/realtime-regen/active/${siteId}`);
  if (!response.ok) {
    throw new Error(`Active real-time regeneration lookup failed (${response.status})`);
  }
  const body = await response.json();
  return body.active
    ? { active: true, jobId: body.jobId, statusUrl: body.statusUrl, progress: body.progress }
    : { active: false };
}

/**
 * Regenerate result types
 */
export interface RegenerateResult {
  success: boolean;
  async?: boolean;
  jobId?: string;
  message?: string;
  error?: string;
  generationId?: string;
}

export interface JobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: {
    percent: number;
    message: string;
  };
  result?: unknown;
  error?: string;
}

/**
 * Get job status (for polling async operations)
 */
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  const response = await authFetch(`/jobs/${jobId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get job status');
  }

  return response.json();
}

/**
 * Regenerate content (currently only supports product_aeo)
 * Returns the durable admission acknowledgement; RegenerationControl observes/resumes it.
 */
export async function regenerateContent(
  contentType: string,
  contentId: string,
  options: { language?: string; requestId: string; signal?: AbortSignal }
): Promise<RegenerateResult & { job: RegenerationJob }> {
  let response: Response;
  try {
    response = await authFetch(`/content-review/${contentType}/${contentId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ language: options.language, async: true, requestId: options.requestId }),
      signal: options.signal,
      timeout: 15000,
    });
  } catch (error) {
    if (!(error instanceof AuthFetchError)) throw error;
    // authFetch preserves its login redirect while retaining the server rejection.
    response = error.response;
  }
  if (!response.ok) {
    const body = await response.text();
    let payload: { code?: unknown } | null;
    try { payload = JSON.parse(body); }
    catch (parseError) { throw new RegenerationRequestError(response.status, body, undefined, parseError); }
    throw new RegenerationRequestError(response.status, body, typeof payload?.code === 'string' ? payload.code : undefined);
  }
  const body = await response.json();
  if (!body.jobId || !body.job || body.job.jobId !== body.jobId) {
    throw new Error('Regeneration acknowledgement is missing its job status. Check the existing request.');
  }
  return body;
}

export interface RegenerationJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: { percent: number; message: string };
  error?: string | null;
}

export async function findRegenerationJob(contentId: string, requestId?: string, signal?: AbortSignal): Promise<RegenerationJob | null> {
  const query = requestId ? `?requestId=${encodeURIComponent(requestId)}` : '';
  const response = await authFetch(`/content-review/product_aeo/${contentId}/regeneration${query}`, { signal, timeout: 15000 });
  if (!response.ok) throw new Error(`Regeneration status lookup returned HTTP ${response.status}: ${await response.text()}`);
  const body = await response.json();
  if (body.job !== null && (!body.job?.jobId || !['pending', 'processing', 'completed', 'failed'].includes(body.job.status))) {
    throw new Error('Regeneration lookup response is invalid');
  }
  return body.job;
}

export async function readRegenerationJob(jobId: string, signal?: AbortSignal): Promise<RegenerationJob> {
  const response = await authFetch(`/jobs/${jobId}`, { signal, timeout: 15000 });
  if (!response.ok) throw new Error(`Job status returned HTTP ${response.status}: ${await response.text()}`);
  const body = await response.json();
  if (body.jobId !== jobId || !['pending', 'processing', 'completed', 'failed'].includes(body.status)) {
    throw new Error('Job status response is invalid. Check the existing request.');
  }
  return body;
}

export async function getContentJSONLD(
  contentType: string,
  contentId: string
): Promise<JSONLDResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/jsonld`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch JSON-LD');
  }

  return response.json();
}

// Helper function to get content type display name
export function getContentTypeLabel(contentType: string): string {
  switch (contentType) {
    case 'ai_article':
      return 'AI Article';
    case 'product_aeo':
      return 'Product AEO';
    case 'category_aeo':
      return 'Category AEO';
    default:
      return contentType;
  }
}

// Helper function to get status display name
export function getStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pending Review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'needs_research':
      return 'Needs Research';
    default:
      return status;
  }
}

// AI Analysis Functions

/**
 * Analyze content with AI (LLM-as-judge) for review assistance
 * Returns quality assessment, issues, and knowledge gap suggestions
 */
export async function analyzeContent(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<AnalysisResponse> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content analysis');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to analyze content');
  }

  return response.json();
}

/**
 * Create feedback entry and knowledge proposals from AI analysis
 * Links the analysis to domain knowledge for future improvement
 */
export async function createKnowledgeProposals(
  contentType: string,
  contentId: string,
  analysis: ContentAnalysis,
  reviewerComment?: string
): Promise<KnowledgeProposalResult> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/analyze/create-proposals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ analysis, reviewerComment }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create knowledge proposals');
  }

  return response.json();
}

// Helper function to get issue type label
export function getIssueTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    TERMINOLOGY: 'Terminology',
    SPECIFICATION: 'Specification',
    MISSING_CONTEXT: 'Missing Context',
    EXPERTISE_SIGNAL: 'Expertise Signal',
    HALLUCINATION: 'Hallucination',
    LOGIC_ERROR: 'Logic Error',
  };
  return labels[type] || type;
}

// Helper function to get severity color
export function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'HIGH':
      return 'destructive';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'secondary';
    default:
      return 'secondary';
  }
}

// Helper function to get recommendation color
export function getRecommendationColor(recommendation: string): 'default' | 'destructive' | 'secondary' {
  switch (recommendation) {
    case 'APPROVE':
      return 'default';
    case 'APPROVE_WITH_NOTES':
      return 'secondary';
    case 'REJECT':
      return 'destructive';
    default:
      return 'secondary';
  }
}

// ============================================
// Evaluation Persistence and Actions
// ============================================

export type ActionType = 'SEND_SUGGESTION' | 'CREATE_DATA_ISSUE' | 'TRIGGER_RESEARCH' | 'FLAG_BUG' | 'DISMISS';
export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'dismissed';

export interface ImprovementAction {
  id: string;
  evaluationId: string;
  itemType: 'issue' | 'gap';
  itemIndex: number;
  actionType: ActionType;
  actionStatus: ActionStatus;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  createdBy?: string;
  createdByName?: string;
  completedBy?: string;
  completedByName?: string;
  completedAt?: string;
  createdAt: string;
}

export interface SavedEvaluation {
  id: string;
  siteId: string;
  contentType: string;
  contentId: string;
  qualityScore: number;
  recommendation: string;
  confidence: string;
  summary: string;
  strengths: string[];
  issuesFound: ContentIssue[];
  knowledgeGaps: KnowledgeGapSuggestion[];
  reviewerNotes: string[];
  rawAnalysis: ContentAnalysis;
  evaluatedBy?: string;
  evaluatorName?: string;
  evaluatedAt: string;
  modelUsed?: string;
  createdAt: string;
  updatedAt: string;
  actions?: ImprovementAction[];
}

export interface SavedEvaluationResponse {
  success: boolean;
  data: SavedEvaluation | null;
  message?: string;
}

// Map issue types to suggested actions
export const ISSUE_TYPE_ACTIONS: Record<string, { action: ActionType; label: string; description: string }> = {
  TERMINOLOGY: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Fix',
    description: 'Send suggestion to super admins to modify generation prompts'
  },
  SPECIFICATION: {
    action: 'CREATE_DATA_ISSUE',
    label: 'Create Issue',
    description: 'Create data quality issue for investigation or AI research'
  },
  MISSING_CONTEXT: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Context',
    description: 'Send suggestion to add missing context'
  },
  EXPERTISE_SIGNAL: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Improvement',
    description: 'Send suggestion to improve expertise signals'
  },
  HALLUCINATION: {
    action: 'FLAG_BUG',
    label: 'Flag Bug',
    description: 'Flag as potential system bug for investigation'
  },
  LOGIC_ERROR: {
    action: 'FLAG_BUG',
    label: 'Flag Bug',
    description: 'Flag as potential logic error in generation'
  }
};

/**
 * Get the latest saved evaluation for a content item
 * Returns null if no evaluation has been saved yet
 */
export async function getSavedEvaluation(
  contentType: string,
  contentId: string
): Promise<SavedEvaluationResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get saved evaluation');
  }

  return response.json();
}

/**
 * Save an AI evaluation for persistence
 * This is called after analyzeContent to persist the results
 */
export async function saveEvaluation(
  contentType: string,
  contentId: string,
  siteId: string,
  analysis: ContentAnalysis,
  modelUsed?: string
): Promise<SavedEvaluationResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, analysis, modelUsed }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save evaluation');
  }

  return response.json();
}

/**
 * Take an action on an issue or gap within an evaluation
 * Actions include: SEND_SUGGESTION, CREATE_DATA_ISSUE, TRIGGER_RESEARCH, FLAG_BUG, DISMISS
 */
export async function takeIssueAction(
  evaluationId: string,
  itemType: 'issue' | 'gap',
  itemIndex: number,
  actionType: ActionType,
  issueData: ContentIssue | KnowledgeGapSuggestion,
  notes?: string
): Promise<{ success: boolean; action: ImprovementAction }> {
  // Use /actions/:evaluationId pattern to avoid route conflicts with /:contentType/:contentId
  const response = await authFetch(`/content-evaluations/actions/${evaluationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      itemType,
      itemIndex,
      actionType,
      issueData,
      notes
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to take action');
  }

  return response.json();
}

/**
 * Get the suggested action for an issue type
 */
export function getSuggestedActionForIssue(issueType: string): { action: ActionType; label: string; description: string } {
  return ISSUE_TYPE_ACTIONS[issueType] || {
    action: 'SEND_SUGGESTION',
    label: 'Send Suggestion',
    description: 'Send suggestion for review'
  };
}

/**
 * Check if an action has been taken for a specific issue/gap
 */
export function hasActionBeenTaken(
  actions: ImprovementAction[] | undefined,
  itemType: 'issue' | 'gap',
  itemIndex: number
): ImprovementAction | undefined {
  if (!actions) return undefined;
  return actions.find(a => a.itemType === itemType && a.itemIndex === itemIndex);
}

// ============================================
// Quality Evolution Tracking (Story 46)
// ============================================

export interface EvaluationHistoryItem {
  id: string;
  qualityScore: number;
  recommendation: string;
  issuesFound: ContentIssue[];
  evaluatedAt: string;
  evaluatorName?: string;
}

export interface EvaluationStats {
  totalEvaluations: number;
  averageScore: number;
  scoreImprovement: number;
  latestScore: number;
  firstScore: number;
}

export interface AllEvaluationsResponse {
  success: boolean;
  data: {
    evaluations: SavedEvaluation[];
    stats: EvaluationStats;
  };
}

export interface VersionHistoryItem {
  historyId: string;
  versionNumber: number;
  createdAt: string;
  evaluation: {
    id: string;
    qualityScore: number;
    recommendation: string;
    issuesCount: number;
    evaluatedAt: string;
  } | null;
}

export interface VersionHistoryResponse {
  success: boolean;
  data: VersionHistoryItem[];
}

export interface ResolutionResult {
  issueIndex: number;
  originalIssue: string;
  status: 'RESOLVED' | 'PARTIALLY_RESOLVED' | 'NOT_RESOLVED';
  explanation: string;
  evidence: string;
}

export interface ResolutionCheckResponse {
  success: boolean;
  data: {
    resolutions: ResolutionResult[];
    overallImprovement: string;
    remainingConcerns: string[];
    summary: {
      total: number;
      resolved: number;
      partiallyResolved: number;
      notResolved: number;
    };
    previousEvaluation: {
      id: string;
      qualityScore: number;
      evaluatedAt: string;
    };
  };
}

/**
 * Get all evaluations for a content item with quality evolution stats
 */
export async function getAllEvaluations(
  contentType: string,
  contentId: string
): Promise<AllEvaluationsResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}/evaluations`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get evaluations');
  }

  return response.json();
}

/**
 * Get version history for a content item with linked evaluations
 */
export async function getVersionHistory(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<VersionHistoryResponse> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/version-history?siteId=${siteId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get version history');
  }

  return response.json();
}

/**
 * Check if issues from a previous evaluation were resolved in new content
 * Uses LLM to compare new content against previous issues
 */
export async function checkIssueResolution(
  contentType: string,
  contentId: string,
  previousEvaluationId: string,
  newContent: Record<string, unknown>
): Promise<ResolutionCheckResponse> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/check-resolution`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ previousEvaluationId, newContent }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check issue resolution');
  }

  return response.json();
}

/**
 * Get evaluation history (simpler version for detecting previous evaluations)
 */
export async function getEvaluationHistory(
  contentType: string,
  contentId: string,
  limit: number = 10
): Promise<{ success: boolean; data: EvaluationHistoryItem[] }> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/history?limit=${limit}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get evaluation history');
  }

  return response.json();
}

// ============================================
// Hallucination Detection (Story 49)
// ============================================

export interface HallucinationDiscrepancy {
  attribute: string;
  claimed: string;
  actual: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  location?: string;
  difference?: string;
}

export interface HallucinationClaim {
  attribute: string;
  value: string;
  unit?: string;
  location?: string;
  confidence: number;
}

export interface HallucinationReport {
  id: string;
  generationId: string;
  productId: string;
  decision: 'PASS' | 'FLAG' | 'BLOCK';
  highestSeverity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  summary: string;
  stats: {
    totalClaims: number;
    verified: number;
    discrepancies: number;
    unverifiable: number;
  };
  discrepancies: HallucinationDiscrepancy[];
  verified?: Array<HallucinationClaim & { feedValue: string; matchType: string }>;
  unverifiable?: Array<HallucinationClaim & { reason: string }>;
  extractedClaims?: HallucinationClaim[];
  feedDataSnapshot?: Record<string, unknown>;
  checkedAt: string;
}

export interface HallucinationReportResponse {
  success: boolean;
  data: HallucinationReport | null;
  message?: string;
}

/**
 * Run hallucination validation on a generation
 * Compares generated content claims against source product feed data
 */
export async function validateSpecs(
  generationId: string
): Promise<HallucinationReportResponse> {
  const response = await authFetch(`/content-evaluations/validate-specs/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate specs');
  }

  return response.json();
}

/**
 * Get existing hallucination validation report for a generation
 */
export async function getHallucinationReport(
  generationId: string
): Promise<HallucinationReportResponse> {
  const response = await authFetch(`/content-evaluations/hallucination-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get hallucination report');
  }

  return response.json();
}

/**
 * Get severity badge color for hallucination discrepancies
 */
export function getHallucinationSeverityColor(severity: string): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Get decision badge color for hallucination validation
 */
export function getHallucinationDecisionColor(decision: string): 'destructive' | 'warning' | 'default' {
  switch (decision) {
    case 'BLOCK':
      return 'destructive';
    case 'FLAG':
      return 'warning';
    case 'PASS':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Knowledge Coverage Scoring (Story 50)
// ============================================

export interface CoverageReport {
  id: string;
  generationId?: string;
  productId: string;
  siteId: string;
  category?: string;

  // Coverage metrics
  totalAttributes: number;
  knownAttributes: number;
  unknownAttributes: string[];
  knownAttributeNames: string[];
  coveragePercentage: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

  // Threshold info
  requiredThreshold?: number;
  belowThreshold: boolean;
  thresholdOverride: boolean;
  priceTier?: 'entry' | 'standard' | 'premium';

  // Metadata
  createdAt: string;
}

export interface CoverageReportResponse {
  success: boolean;
  data: CoverageReport | null;
  message?: string;
}

export interface KnowledgeGap {
  id: string;
  siteId: string;
  category?: string;
  attributeName: string;
  detectionCount: number;
  firstDetectedAt: string;
  lastDetectedAt: string;
  sampleProductIds: string[];
  queuedForResearch: boolean;
  researchPriority?: number;
}

export interface KnowledgeGapsResponse {
  success: boolean;
  data: KnowledgeGap[];
}

/**
 * Check knowledge coverage for a product before generation
 * Returns coverage analysis showing what % of attributes have domain knowledge
 */
export async function checkCoverage(
  productId: string,
  siteId: string,
  options?: { thresholdOverride?: boolean; logGaps?: boolean }
): Promise<CoverageReportResponse> {
  const response = await authFetch('/content-evaluations/check-coverage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId,
      siteId,
      thresholdOverride: options?.thresholdOverride ?? false,
      logGaps: options?.logGaps ?? true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check coverage');
  }

  return response.json();
}

/**
 * Get stored coverage report for a generation
 */
export async function getCoverageReport(
  generationId: string
): Promise<CoverageReportResponse> {
  const response = await authFetch(`/content-evaluations/coverage-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get coverage report');
  }

  return response.json();
}

/**
 * Get top knowledge gaps for a site
 * Returns most frequently detected unknown attributes
 */
export async function getKnowledgeGaps(
  siteId: string,
  options?: { category?: string; limit?: number; queuedOnly?: boolean }
): Promise<KnowledgeGapsResponse> {
  const params = new URLSearchParams();
  params.set('siteId', siteId);
  if (options?.category) params.set('category', options.category);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.queuedOnly) params.set('queuedOnly', 'true');

  const response = await authFetch(`/content-evaluations/knowledge-gaps?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get knowledge gaps');
  }

  return response.json();
}

/**
 * Get risk level badge color for coverage scoring
 */
export function getCoverageRiskColor(riskLevel: string): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (riskLevel) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get coverage percentage color based on thresholds
 */
export function getCoveragePercentageColor(percentage: number): string {
  if (percentage >= 75) return 'text-green-600';
  if (percentage >= 50) return 'text-yellow-600';
  if (percentage >= 25) return 'text-orange-600';
  return 'text-red-600';
}

// ============================================
// Persona Validation (Story 51)
// ============================================

export interface PersonaDetail {
  id: string;
  name: string;
  description: string;
}

export interface DetectedPersona {
  persona: string;
  personaName?: string;
  confidence: number;
  evidence: string[];
  impliedTier: 'entry' | 'mid_range' | 'premium' | 'ultra_premium' | 'unknown';
}

export interface PersonaValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Price tier info
  productPrice: number;
  priceTier: 'entry' | 'mid_range' | 'premium' | 'ultra_premium';
  priceTierDisplay: string;
  category?: string;
  validPersonas: PersonaDetail[];

  // Detection results
  detectedPersonas: DetectedPersona[];
  overallTone?: string;
  toneExplanation?: string;

  // Validation results
  alignmentScore: number;
  isAligned: boolean;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  mismatchReason?: string;
  recommendations: string[];

  validatedAt: string;
}

export interface PersonaValidationResponse {
  success: boolean;
  data: PersonaValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

export interface PersonaGuidance {
  promptAddition: string;
  toneGuidance: string;
  vocabularyGuidance: string[];
  avoidTerms: string[];
}

export interface PersonaTierInfo {
  tier: string;
  tierDisplayName: string;
  validPersonas: string[];
  validPersonaDetails: PersonaDetail[];
  priceRange: { min: number; max: number | null };
  category: string;
}

export interface PersonaGuidanceResponse {
  success: boolean;
  data: {
    tier: PersonaTierInfo;
    guidance: PersonaGuidance;
  } | null;
  message?: string;
}

/**
 * Run persona validation on a generation
 * Compares content targeting against product price tier
 */
export async function validatePersona(
  generationId: string
): Promise<PersonaValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-persona/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate persona');
  }

  return response.json();
}

/**
 * Get existing persona validation report for a generation
 */
export async function getPersonaReport(
  generationId: string
): Promise<PersonaValidationResponse> {
  const response = await authFetch(`/content-evaluations/persona-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get persona report');
  }

  return response.json();
}

/**
 * Get persona guidance for content generation
 */
export async function getPersonaGuidance(
  price: number,
  category?: string
): Promise<PersonaGuidanceResponse> {
  const response = await authFetch('/content-evaluations/persona-guidance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ price, category }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get persona guidance');
  }

  return response.json();
}

/**
 * Get severity badge color for persona validation
 */
export function getPersonaSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get alignment status color
 */
export function getPersonaAlignmentColor(isAligned: boolean, score: number): string {
  if (isAligned && score >= 80) return 'text-green-600';
  if (isAligned) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get tier badge variant
 */
export function getTierBadgeVariant(tier: string): 'default' | 'secondary' | 'outline' {
  switch (tier) {
    case 'ultra_premium':
    case 'premium':
      return 'default';
    case 'mid_range':
      return 'secondary';
    default:
      return 'outline';
  }
}

// ============================================
// Category Coherence Validation (Story 52)
// ============================================

export interface ExtractedAlternative {
  name: string;
  brand?: string;
  category: string;
  categoryDisplay?: string;
  context?: string;
  excerpt?: string;
}

export interface CoherenceIssue {
  alternative: string;
  brand?: string;
  category: string;
  reason: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  excerpt?: string;
}

export interface CoherenceValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Category info
  productCategory: string;
  productCategoryDisplay: string;
  discipline?: string;
  compatibleCategories: string[];

  // Alternatives
  extractedAlternatives: ExtractedAlternative[];
  compatibleAlternatives: ExtractedAlternative[];
  incompatibleAlternatives: ExtractedAlternative[];

  // Scoring
  coherenceScore: number | null;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  message: string;
  issues: CoherenceIssue[];

  validatedAt: string;
}

export interface CoherenceValidationResponse {
  success: boolean;
  data: CoherenceValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

/**
 * Run category coherence validation on a generation
 * Checks that alternatives are from compatible categories
 */
export async function validateCoherence(
  generationId: string
): Promise<CoherenceValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-coherence/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate coherence');
  }

  return response.json();
}

/**
 * Get existing coherence validation report for a generation
 */
export async function getCoherenceReport(
  generationId: string
): Promise<CoherenceValidationResponse> {
  const response = await authFetch(`/content-evaluations/coherence-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get coherence report');
  }

  return response.json();
}

/**
 * Get coherence score color based on value
 */
export function getCoherenceScoreColor(score: number | null): string {
  if (score === null) return 'text-gray-500';
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get coherence severity badge color
 */
export function getCoherenceSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Completeness Validation (Story 53)
// ============================================

export interface SpecRequirement {
  spec: string;
  displayName: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  aliases?: string[];
  conditional?: boolean;
  triggeredBy?: string;
}

export interface FoundSpec extends SpecRequirement {
  status: 'found';
  value: string;
  location?: string;
  confidence: number;
}

export interface MissingSpec extends SpecRequirement {
  status: 'missing';
}

export interface PartialSpec extends SpecRequirement {
  status: 'partial';
  foundValue: string;
  confidence: number;
}

export interface CompletenessValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Context
  category: string;
  priceTier: string;
  productAttributes: Record<string, boolean>;

  // Checklist
  requiredSpecs: SpecRequirement[];
  conditionalSpecs: SpecRequirement[];
  totalRequired: number;

  // Results
  foundSpecs: FoundSpec[];
  missingSpecs: MissingSpec[];
  partialSpecs: PartialSpec[];

  // Scoring
  completenessScore: number;
  weightedScore: number;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  decision: 'PASS' | 'FLAG' | 'BLOCK';
  missingCriticalCount: number;
  message: string;

  validatedAt: string;
}

export interface CompletenessValidationResponse {
  success: boolean;
  data: CompletenessValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

/**
 * Run completeness validation on a generation
 * Checks that content includes all required specs
 */
export async function validateCompleteness(
  generationId: string
): Promise<CompletenessValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-completeness/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate completeness');
  }

  return response.json();
}

/**
 * Get existing completeness validation report for a generation
 */
export async function getCompletenessReport(
  generationId: string
): Promise<CompletenessValidationResponse> {
  const response = await authFetch(`/content-evaluations/completeness-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get completeness report');
  }

  return response.json();
}

/**
 * Get completeness score color based on value
 */
export function getCompletenessScoreColor(score: number): string {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get decision badge variant
 */
export function getCompletenessDecisionVariant(decision: string): 'default' | 'destructive' | 'warning' {
  switch (decision) {
    case 'PASS':
      return 'default';
    case 'FLAG':
      return 'warning';
    case 'BLOCK':
      return 'destructive';
    default:
      return 'default';
  }
}

/**
 * Get completeness severity badge color
 */
export function getCompletenessSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Deployment Verification
// ============================================

export interface DeploymentVerificationResult {
  success: boolean;
  url?: string;
  productTitle?: string;
  faqStatus?: 'deployed' | 'partial' | 'modified' | 'not_found' | 'page_error' | 'js_required';
  faqSimilarity?: number;
  details?: {
    ourCount: number;
    liveCount: number;
    matchedCount: number;
    unmatchedCount: number;
    matches?: Array<{
      ourQuestion: string;
      liveQuestion: string;
      questionSimilarity: number;
      answerSimilarity: number;
      combinedSimilarity: number;
    }>;
    unmatchedQuestions?: string[];
    liveFaqSource?: string;
    checkedAt?: string;
  };
  error?: string;
  message?: string;
}

export interface DeploymentVerificationResponse {
  success: boolean;
  data: DeploymentVerificationResult;
}

export interface DeploymentVerificationStats {
  deployed: number;
  partial: number;
  modified: number;
  notFound: number;
  errors: number;
  jsRequired: number;
  totalVerified: number;
  pendingVerification: number;
  avgSimilarity: number;
  lastVerified: string | null;
}

export interface DeploymentStatsResponse {
  success: boolean;
  data: DeploymentVerificationStats;
}

/**
 * Verify deployment status for a single AEO item
 * Checks if the FAQs have been deployed to the live customer page
 */
export async function verifyDeployment(
  contentType: string,
  contentId: string
): Promise<DeploymentVerificationResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/verify-deployment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify deployment');
  }

  return response.json();
}

/**
 * Get deployment verification statistics for a site
 */
export async function getDeploymentStats(
  siteId: string
): Promise<DeploymentStatsResponse> {
  const response = await authFetch(`/content-review/verification-stats/${siteId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get deployment stats');
  }

  return response.json();
}

/**
 * Get FAQ status badge color
 */
export function getFaqStatusColor(status: string): 'default' | 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'deployed':
      return 'default';
    case 'partial':
    case 'modified':
      return 'warning';
    case 'not_found':
    case 'page_error':
    case 'js_required':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Get FAQ status display label
 */
export function getFaqStatusLabel(status: string): string {
  switch (status) {
    case 'deployed':
      return 'Deployed';
    case 'partial':
      return 'Partially Deployed';
    case 'modified':
      return 'Modified';
    case 'not_found':
      return 'Not Found';
    case 'page_error':
      return 'Page Error';
    case 'js_required':
      return 'JS Required';
    default:
      return status;
  }
}

/**
 * ⛔ THE POLL DECISION, EXTRACTED SO IT CAN BE TESTED BEHAVIOURALLY RATHER THAN ASSERTED ABOUT.
 * codex asked for tests of completion / 5-failure / stalled-progress. The logic lived inside a
 * useEffect in a component that needs SiteContext, a router, toasts and a dozen services to render,
 * so testing it in place would have meant a source-text assertion — and a source-text assertion of
 * BRANCHING logic is the weakest thing available: it certifies the code's shape, not its decisions.
 * ⇒ Extracting it is what makes a real test possible. Pure in / pure out, no timers, no DOM.
 */
export type PollDecision =
  | { kind: 'continue'; progress: { percent: number; message: string } }
  | { kind: 'completed'; progress: { percent: number; message: string }; result: RealtimeRegenJob['result'] }
  | { kind: 'aborted'; error: string }
  | { kind: 'tracking-lost'; reason: 'poll-errors' | 'stalled' };

export interface PollState {
  /** consecutive failed polls; a success resets it to 0 */
  failures: number;
  /** status|percent|message of the last poll, to detect a row that stops changing */
  signature: string;
  /** epoch ms when the signature last changed */
  lastChangeAt: number;
}

export const MAX_POLL_FAILURES = 5;
/**
 * ⚠️ MUST EXCEED THE SLOWEST LEGITIMATE STEP. One product takes ~90-120s, so anything under ~2 min
 * would declare a HEALTHY run dead mid-product. 5 min is >2x that — deliberately generous, because a
 * false "tracking lost" costs a needless re-run of an expensive cohort.
 */
export const STALL_MS = 5 * 60 * 1000;

/** A poll that threw. Transient until MAX_POLL_FAILURES consecutive ones. */
export function onPollError(state: PollState): { decision: PollDecision; state: PollState } {
  const failures = state.failures + 1;
  return failures >= MAX_POLL_FAILURES
    ? { decision: { kind: 'tracking-lost', reason: 'poll-errors' }, state: { ...state, failures } }
    : { decision: { kind: 'continue', progress: { percent: 0, message: '' } }, state: { ...state, failures } };
}

/**
 * A poll that SUCCEEDED. ⛔ Note an abandoned job arrives here, not in onPollError: the row is stuck
 * at 'processing' and every poll succeeds, so the failure counter never increments. Different
 * mechanism, same symptom — which is why stall detection lives on THIS path.
 */
export function onPollSuccess(
  job: RealtimeRegenJob,
  state: PollState,
  now: number
): { decision: PollDecision; state: PollState } {
  const progress = { percent: job.progress?.percent ?? 0, message: job.progress?.message ?? '' };
  const signature = `${job.status}|${progress.percent}|${progress.message}`;
  const changed = signature !== state.signature;
  const next: PollState = {
    failures: 0,
    signature,
    lastChangeAt: changed ? now : state.lastChangeAt,
  };

  if (job.status === 'completed') return { decision: { kind: 'completed', progress, result: job.result }, state: next };
  if (job.status === 'failed') return { decision: { kind: 'aborted', error: job.error || 'unknown error' }, state: next };
  if (!changed && now - state.lastChangeAt > STALL_MS) {
    return { decision: { kind: 'tracking-lost', reason: 'stalled' }, state: next };
  }
  return { decision: { kind: 'continue', progress }, state: next };
}
